import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import { formatDateTimePickerValue } from 'hooks/date/formattedTime'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ITenderProtocolData } from 'types/final-protocol'

const StyledTableCell = styled(TableCell)(() => ({
  padding: 0,
  width: '100%',

  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    borderRadius: 0,
  },

  '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled': {
    background: 'rgba(128,128,128, 0.1)',
  },
}))

type Props = {
  protocolData: ITenderProtocolData
}

export const SubmitterOrganizationsInfoTable = ({ protocolData }: Props) => {
  const { t } = useTranslation()
  const { control } = useFormContext()

  const { fields } = useFieldArray({
    control,
    name: `submitterOrganizations`,
  })

  return (
    <TableContainer>
      <Table
        sx={{
          tableLayout: 'fixed',
          background: '#FFF',
          '& td, th': {
            border: '1px solid #ccc',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('ordinal')}</TableCell>
            <TableCell>{t('supplier-name')}</TableCell>
            <TableCell>{t('country')}</TableCell>
            <TableCell>{t('tin')}</TableCell>
            <TableCell>{t('tender-proposal-submitted-date')}</TableCell>
            <TableCell>{t('subject-info')}</TableCell>
            <TableCell>{t('delivery-terms')}</TableCell>
            <TableCell>{t('proposal-price-exchange')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((org, index) => {
            return (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <StyledTableCell>
                  <Controller
                    name={`submitterOrganizations.${index}.orgName`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth variant="outlined" />
                    )}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Controller
                    name={`submitterOrganizations.${index}.orgCountry`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <TextField {...field} fullWidth variant="outlined" />
                      </>
                    )}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Controller
                    name={`submitterOrganizations.${index}.orgTaxId`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <TextField {...field} fullWidth variant="outlined" />
                      </>
                    )}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Controller
                    control={control}
                    name={`submitterOrganizations.${index}.submittedAt`}
                    render={({ field }) => (
                      <DateTimePicker
                        {...field}
                        ampm={false}
                        value={formatDateTimePickerValue(
                          protocolData.submitterOrganizations[index].submittedAt
                        )}
                        slotProps={{ textField: { error: false, fullWidth: true } }}
                        onChange={field.onChange}
                        timeSteps={{
                          hours: 1,
                          minutes: 1,
                        }}
                      />
                    )}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Controller
                    name={`submitterOrganizations.${index}.businessSizeRemark`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth variant="outlined" />
                    )}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Controller
                    name={`submitterOrganizations.${index}.deliveryTerms`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth variant="outlined" />
                    )}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Controller
                    name={`submitterOrganizations.${index}.bidPriceAndCurrency`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth variant="outlined" />
                    )}
                  />
                </StyledTableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
